import { useGetAsset } from '@/hooks/api/assets/useGetAsset';
import { api } from '@/services/api';
import { AssetState } from '@/api/generatedApi';
import { Box, CircularProgress, styled } from '@mui/material';
import { AxiosError } from 'axios';
import { useEffect, useRef, useState } from 'react';
import { AssetSidebar } from '../AssetSidebar';
import { DependencyDetailPopover } from './DependencyDetailPopover';
import { AssetErrorMessage } from './AssetErrorMessage';
import { useSearchFilters } from '@/hooks/useSearchFilters';
import { useCriticalGeometryPut } from '@/hooks/assetViewer/useCriticalGeometryPut';
import { useThemeMode } from '@/hooks/useThemeMode';

const MainContentArea = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'row',
  height: '100%',
  overflow: 'hidden',
});

const AssetView = styled(Box)(({ theme }) => ({
  flexDirection: 'column',
  flex: '2 1 auto',
  minWidth: '200px',
  display: 'flex',
  backgroundColor: theme.palette.background.default,
}));

const AssetIframe = styled('iframe')({
  width: '100%',
  height: '100%',
  border: 0,
});

const ZeroStateBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
});

export const AssetViewer = ({ assetId }: { assetId?: string }) => {
  const { selectedTenantId: assetTenantId } = useSearchFilters();
  const { resolvedMode } = useThemeMode();

  const {
    asset,
    error: assetError,
    isLoading: assetIsLoading,
  } = useGetAsset({
    assetId: assetId ?? '',
    assetTenantId,
  });
  const [error, setError] = useState<string>();
  const iframeReference = useRef<HTMLIFrameElement>(null);
  const contentReference = useRef<HTMLDivElement>(null);
  const [selectedTreeItemId, setSelectedTreeItemId] = useState<string>('');
  const { put: handleReprocessCriticalGeometry, isPutInFlight: isReprocessInFlight } = useCriticalGeometryPut({
    assetId,
    tenantId: assetTenantId,
    iframeRef: iframeReference,
    assetType: asset?.type,
  });

  const isUnsupported = asset?.state === AssetState.Unsupported;
  const isIndexing = asset?.state === AssetState.Indexing;

  const handleTreeItemClick = (itemId: string) => {
    setSelectedTreeItemId(itemId);
  };

  const handlePopoverClose = () => {
    setSelectedTreeItemId('');
  };

  const fetchViewer = async () => {
    setError(undefined);
    try {
      const apiLocation = `${import.meta.env.VITE_API_BASE_URL}/v3`;

      const {
        data: { token },
      } = await api.viewer.viewerToken();

      const iframeSource = `${apiLocation}/tenants/${assetTenantId}/viewer/asset?token=${token}&assetId=${assetId}&theme=${resolvedMode}`;

      if (iframeReference.current) {
        iframeReference.current.src = iframeSource;
      }
    } catch (error) {
      setError(error instanceof AxiosError ? error.message : 'Error fetching viewer');
    }
  };

  useEffect(() => {
    if (assetId && !assetError && !assetIsLoading && !isUnsupported && !isIndexing) {
      fetchViewer();
    }
  }, [assetId, assetTenantId, assetError, assetIsLoading, asset?.state]);

  useEffect(() => {
    const iframe = iframeReference.current;
    if (!iframe?.contentWindow || !iframe.src) return;
    const targetOrigin = new URL(iframe.src).origin;
    iframe.contentWindow.postMessage({ type: 'physna:theme-mode', mode: resolvedMode }, targetOrigin);
  }, [resolvedMode]);

  if (assetIsLoading) {
    return (
      <ZeroStateBox>
        <CircularProgress />
      </ZeroStateBox>
    );
  }

  if (assetError) {
    return <AssetErrorMessage errorMessage={assetError.message} />;
  }

  return (
    <MainContentArea>
      <AssetSidebar
        asset={asset}
        onTreeItemClick={handleTreeItemClick}
        onReprocessCriticalGeometry={handleReprocessCriticalGeometry}
        isReprocessCriticalGeometryDisabled={!assetId || !assetTenantId || isReprocessInFlight}
      />
      <AssetView ref={contentReference}>
        {isUnsupported && <AssetErrorMessage assetState={AssetState.Unsupported} />}
        {!isUnsupported && isIndexing && <AssetErrorMessage assetState={AssetState.Indexing} />}
        {!isUnsupported && !isIndexing && (
          <>
            <AssetIframe
              key={assetId}
              ref={iframeReference}
              title='3D Model Viewer'
              sandbox='allow-same-origin allow-scripts allow-popups allow-forms allow-popups-to-escape-sandbox'
              referrerPolicy='no-referrer'
            />
            {error && <div>{error}</div>}
          </>
        )}
        <DependencyDetailPopover
          open={!!selectedTreeItemId}
          anchorEl={contentReference.current}
          selectedTreeItemId={selectedTreeItemId}
          onClose={handlePopoverClose}
        />
      </AssetView>
    </MainContentArea>
  );
};
